import React, { Dispatch, SetStateAction } from 'react';
import { Column } from 'react-table';
import useGetTenantGroups from 'Hooks/query-hooks/tenant-groups/useGetTenantGroups';

import Table from 'Components/Table';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import DeleteTenantGroupModal from '../DeleteTenantGroupModal';
import styles from './TenantGroupsTable.css';

type TenantGroupTableOptions = {
    isScheduledDeploymentRevision: boolean;
};

type TenantGroupTableProps = {
    mode: 'revisionWindow' | 'manageTenantGroups';
    columns: Column<TenantGroup>[];
    deploymentData?: Deployment;
    deleteTenantGroup?: TenantGroup;
    toggleModal?: boolean;
    setToggleModal?: Dispatch<SetStateAction<boolean>>;
    setShowSingleTenantGroup?: Dispatch<SetStateAction<boolean>>;
    setCurrTenantGroup?: Dispatch<SetStateAction<TenantGroup>>;
    options?: Partial<TenantGroupTableOptions>;
};

const TenantGroupsTable = ({
    mode,
    columns,
    deleteTenantGroup,
    deploymentData,
    toggleModal,
    setToggleModal,
    setShowSingleTenantGroup,
    setCurrTenantGroup,
    options
}: TenantGroupTableProps) => {
    const isRevisionWindow = mode === 'revisionWindow';
    const getTenantGroups = useGetTenantGroups({ disable: isRevisionWindow });

    const handleTrackEvent = () => {
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier: 'Tenant Groups - View Tenant Group',
                text: 'View Tenant Group'
            }
        });
    };

    const revisionTenantGroups = React.useMemo(() => {
        const tenantGroupIdsOfCurrentRevision = Object.keys(deploymentData?.tenantGroupRevisions ?? []);
        const tenantGroupsOfCurrentRevision: TenantGroups = [];

        if (options?.isScheduledDeploymentRevision) {
            deploymentData?.tenantGroupData?.forEach((tenantGroup) => {
                const tenantGroupObject = {
                    id: tenantGroup.tenantGroupId,
                    name: tenantGroup.tenantGroupName,
                    description: tenantGroup.tenantGroupDescription,
                    numberOfTenants: tenantGroup?.tenantGroupTenantIds?.length ?? 0
                };
                tenantGroupsOfCurrentRevision.push(tenantGroupObject);
            });
            return tenantGroupsOfCurrentRevision;
        }

        if (deploymentData?.tenantGroupRevisions) {
            deploymentData?.tenantGroupData?.forEach((tenantGroup) => {
                if (tenantGroupIdsOfCurrentRevision.includes(String(tenantGroup.tenantGroupId))) {
                    const tenantGroupObject = {
                        id: tenantGroup.tenantGroupId,
                        name: tenantGroup.tenantGroupName,
                        description: tenantGroup.tenantGroupDescription,
                        numberOfTenants: tenantGroup?.tenantGroupTenantIds?.length ?? 0
                    };
                    tenantGroupsOfCurrentRevision.push(tenantGroupObject);
                }
            });
        }

        return tenantGroupsOfCurrentRevision;
    }, [deploymentData, options]);

    const tenantGroups = React.useMemo(() => getTenantGroups.data, [getTenantGroups.data]);

    return (
        <div className={styles.tableContainer}>
            <Table
                tableName='tenant-groups-table'
                showMultiSort
                showRichFilter
                pathToIdOnDataObj='name'
                enablePagination
                initialState={{
                    sortBy: [{ id: 'name', desc: false }],
                    hiddenColumns: ['id']
                }}
                dataTestId='tenant-groups-table'
                noDataMessage='No Tenant Groups found'
                columns={columns}
                data={isRevisionWindow ? revisionTenantGroups ?? [] : tenantGroups?.original ?? []}
                showExpandableSearch
                clickableRow
                isLoading={getTenantGroups.isInitialLoading}
                onRowInteractCB={(row) => {
                    if (setShowSingleTenantGroup && setCurrTenantGroup) {
                        setShowSingleTenantGroup(true);
                        setCurrTenantGroup(row.original);
                    }
                    handleTrackEvent();
                }}
            />
            {deleteTenantGroup && (
                <DeleteTenantGroupModal
                    closeModal={() => setToggleModal?.(false)}
                    isOpen={toggleModal ?? false}
                    tenantGroup={deleteTenantGroup}
                />
            )}
        </div>
    );
};

export default React.memo(TenantGroupsTable);
